import classNames from "classnames";
import { useRef, useEffect, forwardRef, useLayoutEffect, useCallback } from "react";
import styled from "styled-components/macro";
import { useProjectStore } from "../../stores/projectStore.js";

const DynamicInputContainer = styled.div`
  display: flex;
  flex-direction: row;
  font-family: Inter, sans-serif;
  width: 100%;
  input {
    /* field-sizing: content; // when the day comes that this is supported in all browsers, we can use this instead of the javascript calculations. */
    width: 100%;
  }
`;
/**
 * Note on usage:
 * This component is an Input inside a Container. The Container will ALWAYS
 * grow to fit the available space. The Input will grow to fit the text content up
 * UNTIL the Container width OR the props.maxWidth, whichever is SMALLER.
 *
 * This means that no matter the props.maxWidth, the Input will NEVER be wider than
 * the parent container to prevent overflow problems.
 *
 * Notes on debugging:
 *
 * If the Input isn't expanding even though there seems to be space available,
 * check any parent containers OUTSIDE OF this component that
 * may be constraining the width (I'm looking at you, random wrapping divs).
 *
 * Margins mess up flexbox calculations. If something is overflowing, see
 * if there are any margins.
 *
 * @param {any} props.resizeTrigger - will re-size input when this value changes.
 * if not provided, will re-size when the center column width changes (e.g. when the window is resized).
 * @param {number} props.edgeBuffer - hardcoded buffer to account for unavoidable margin,
 * borders, etc and still prevent overflow. Defaults to 10px. Use as a last resort.
 * @returns {React.ReactNode}
 */
const DynamicInputLength = forwardRef(function (
  {
    //
    placeholder = "Type something",
    maxLength = 50,
    minWidth = 50,
    maxWidth = 500,
    value,
    onChange,
    onFocus,
    className,
    disabled,
    resizeTrigger,
    edgeBuffer = 10,
  },
  parentRef
) {
  const inputRef = useRef(null);
  const textMeasurerRef = useRef(null);
  const containerRef = useRef(null);
  const columnWidths = useProjectStore((state) => state.columnWidths);
  const actualResizeTrigger = resizeTrigger ? resizeTrigger : columnWidths.center;

  const handleResize = useCallback(() => {
    if (textMeasurerRef.current && inputRef.current && containerRef.current) {
      const textWidth = textMeasurerRef.current.offsetWidth;
      inputRef.current.style.width = textWidth + 4 + "px";
    }
  }, []);

  // Calculate the width of the container without the input length affecting it.
  // This is how we determine how much space is available for the input.
  // UseLayoutEffect to avoid flickering.
  useLayoutEffect(() => {
    if (containerRef.current && inputRef.current) {
      inputRef.current.style.width = minWidth + "px";
      const calculatedMaxWidth = containerRef.current.offsetWidth - edgeBuffer;

      if (calculatedMaxWidth < maxWidth) {
        inputRef.current.style.maxWidth = calculatedMaxWidth + "px";
      } else {
        inputRef.current.style.maxWidth = maxWidth + "px";
      }
      handleResize();
    }
  }, [actualResizeTrigger, minWidth, maxWidth, edgeBuffer, handleResize]);

  useEffect(() => {
    handleResize();
  }, [value, handleResize]);

  return (
    <DynamicInputContainer ref={containerRef}>
      <span
        ref={textMeasurerRef}
        className={classNames({ [className]: className })}
        style={{
          position: "absolute",
          visibility: "hidden",
          whiteSpace: "pre",
        }}
      >
        {value || placeholder}
      </span>
      <input
        type="text"
        ref={(ref) => {
          inputRef.current = ref;
          if (parentRef) {
            parentRef.current = ref;
          }
        }}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        style={{
          minWidth: `${minWidth}px`,
          whiteSpace: "pre",
        }}
        maxLength={maxLength}
        placeholder={placeholder}
        className={classNames({ [className]: className })}
        disabled={disabled}
      />
    </DynamicInputContainer>
  );
});

export default DynamicInputLength;
